import { z } from "zod";
import { isPhoneComplete } from "@/shared/utils";

const dateOptional = z.string().optional().or(z.literal(""));

export const editGeneralSchema = z
  .object({
    surname: z.string().min(1, "personalSheet.validation.required"),
    name: z.string().min(1, "personalSheet.validation.required"),
    father_name: z.string().optional().or(z.literal("")),
    family_status: z.string().min(1, "personalSheet.validation.required"),
    citizenship: z.string().min(1, "personalSheet.validation.required"),
    nationality: z.string().min(1, "personalSheet.validation.required"),
    city_of_birth_id: z.number().nullable(),
    city_of_birth_other: z.string().nullable(),
    is_resident: z.boolean(),
    is_student: z.boolean(),
    enrollment_start_date: z.string().nullable(),
    enrollment_end_date: z.string().nullable(),
  })
  .superRefine(function validateGeneralFields(data, ctx) {
    if (data.city_of_birth_id == null && (!data.city_of_birth_other || data.city_of_birth_other.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "personalSheet.validation.required",
        path: ["city_of_birth_id"],
      });
    }
    if (data.is_student) {
      if (!data.enrollment_start_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "personalSheet.validation.required",
          path: ["enrollment_start_date"],
        });
      }
      if (!data.enrollment_end_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "personalSheet.validation.required",
          path: ["enrollment_end_date"],
        });
      }
      if (data.enrollment_start_date && data.enrollment_end_date && data.enrollment_end_date < data.enrollment_start_date) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "personalSheet.validation.endDateBeforeStart",
          path: ["enrollment_end_date"],
        });
      }
    }
  });

export type EditGeneralValues = z.infer<typeof editGeneralSchema>;

export const editContactsSchema = z.object({
  phone: z
    .string()
    .min(1, "personalSheet.validation.required")
    .refine(isPhoneComplete, "personalSheet.validation.invalidPhone"),
  phone_additional: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((value) => !value || isPhoneComplete(value), "personalSheet.validation.invalidPhone"),
});

export type EditContactsValues = z.infer<typeof editContactsSchema>;

export const editAddressesSchema = z.object({
  address_registration: z.string().min(1, "personalSheet.validation.required"),
  address_factual: z.string().min(1, "personalSheet.validation.required"),
});

export type EditAddressesValues = z.infer<typeof editAddressesSchema>;

export const editEmergencyContactSchema = z.object({
  contact_name: z.string().min(1, "personalSheet.validation.required"),
  contact_phone: z
    .string()
    .min(1, "personalSheet.validation.required")
    .refine(isPhoneComplete, "personalSheet.validation.invalidPhone"),
  contact_relation: z.string().min(1, "personalSheet.validation.required"),
});

export type EditEmergencyContactValues = z.infer<typeof editEmergencyContactSchema>;

export const editIdDocumentsSchema = z
  .object({
    national_id_number: z.string().min(1, "personalSheet.validation.required"),
    national_id_issue_date: z.string().min(1, "personalSheet.validation.required"),
    national_id_expiry_date: z.string().min(1, "personalSheet.validation.required"),
    national_id_issued_by: z.string().min(1, "personalSheet.validation.required"),
  })
  .refine(
    function validateExpiryAfterIssue(data) {
      if (!data.national_id_issue_date || !data.national_id_expiry_date) return true;
      return data.national_id_expiry_date > data.national_id_issue_date;
    },
    {
      message: "personalSheet.validation.expiryBeforeIssue",
      path: ["national_id_expiry_date"],
    },
  );

export type EditIdDocumentsValues = z.infer<typeof editIdDocumentsSchema>;

export const editBankingSchema = z.object({
  bank_name: z.string().min(1, "personalSheet.validation.required"),
  iban_account: z
    .string()
    .min(1, "personalSheet.validation.required")
    .length(20, "personalSheet.validation.ibanLength"),
  bik_number: z
    .string()
    .min(1, "personalSheet.validation.required")
    .min(8, "personalSheet.validation.bikLength")
    .max(11, "personalSheet.validation.bikLength"),
});

export type EditBankingValues = z.infer<typeof editBankingSchema>;

export const editEducationEntrySchema = z.object({
  degree: z.string().min(1, "personalSheet.validation.required"),
  university_name: z.string().min(1, "personalSheet.validation.required"),
  specialty: z.string().min(1, "personalSheet.validation.required"),
  admission_year: z.string().min(1, "personalSheet.validation.required"),
  graduation_year: z.string().min(1, "personalSheet.validation.required"),
  diploma_number: z.string().min(1, "personalSheet.validation.required"),
});

export type EditEducationEntryValues = z.infer<typeof editEducationEntrySchema>;

export const editEducationIncompleteEntrySchema = z.object({
  degree: z.string().min(1, "personalSheet.validation.required"),
  university_name: z.string().min(1, "personalSheet.validation.required"),
  specialty: z.string().min(1, "personalSheet.validation.required"),
  admission_year: z.string().min(1, "personalSheet.validation.required"),
  left_from_course: z.string().min(1, "personalSheet.validation.required"),
});

export type EditEducationIncompleteEntryValues = z.infer<typeof editEducationIncompleteEntrySchema>;

export const editSocialCategoryEntrySchema = z.object({
  category_type: z.string().min(1, "personalSheet.validation.required"),
  issue_date: z.string().min(1, "personalSheet.validation.required"),
  expiry_date: dateOptional,
  notes: z.string().optional(),
});

export type EditSocialCategoryEntryValues = z.infer<typeof editSocialCategoryEntrySchema>;

export const editExperienceEntrySchema = z.object({
  organization_name: z.string().min(1, "personalSheet.validation.required"),
  speciality: z.string().min(1, "personalSheet.validation.required"),
  work_position: z.string().min(1, "personalSheet.validation.required"),
  work_format: z.enum(["onsite", "remote", "hybrid"], {
    message: "personalSheet.validation.required",
  }),
  work_address: z.string().min(1, "personalSheet.validation.required"),
  beginning_date: z.string().min(1, "personalSheet.validation.required"),
  end_date: dateOptional,
});

export type EditExperienceEntryValues = z.infer<typeof editExperienceEntrySchema>;

export const editLanguageEntrySchema = z.object({
  language: z.string().min(1, "personalSheet.validation.required"),
  proficiency_level: z.string().min(1, "personalSheet.validation.required"),
});

export type EditLanguageEntryValues = z.infer<typeof editLanguageEntrySchema>;

export const editScientificWorkEntrySchema = z.object({
  title: z.string().min(1, "personalSheet.validation.required"),
  year: z.string().min(1, "personalSheet.validation.required"),
});

export type EditScientificWorkEntryValues = z.infer<typeof editScientificWorkEntrySchema>;

export const editGovernmentAwardEntrySchema = z.object({
  award_name: z.string().min(1, "personalSheet.validation.required"),
  issued_by: z.string().min(1, "personalSheet.validation.required"),
  issued_date: z.string().min(1, "personalSheet.validation.required"),
});

export type EditGovernmentAwardEntryValues = z.infer<typeof editGovernmentAwardEntrySchema>;

export function createEditIdDocumentsSchema(gender: string) {
  const optionalStr = z.string().optional().or(z.literal(""));
  return z
    .object({
      national_id_number: z.string().min(1, "personalSheet.validation.required"),
      national_id_issue_date: z.string().min(1, "personalSheet.validation.required"),
      national_id_expiry_date: z.string().min(1, "personalSheet.validation.required"),
      national_id_issued_by: z.string().min(1, "personalSheet.validation.required"),
      military_obligation_relation: gender === "male" ? z.string().min(1, "personalSheet.validation.required") : optionalStr,
      military_rank: optionalStr,
      military_composition: optionalStr,
      military_branch: optionalStr,
    })
    .refine(
      function validateExpiryAfterIssue(data) {
        if (!data.national_id_issue_date || !data.national_id_expiry_date) return true;
        return data.national_id_expiry_date > data.national_id_issue_date;
      },
      { message: "personalSheet.validation.expiryBeforeIssue", path: ["national_id_expiry_date"] },
    );
}

export type EditIdDocumentsFullValues = z.infer<ReturnType<typeof createEditIdDocumentsSchema>>;
